import {
  resolveWebsiteUrl,
  type WebsiteLocale,
  type WebsitePage,
} from '@affine/env/website';
import { Service } from '@toeverything/infra';

import { GlobalDialogService } from '../../dialogs';
import { UrlService } from '../../url';

export function sanitizeFeedbackPrefill(value?: string, maxLength = 16_000) {
  return value
    ?.replace(
      /\bauthorization\s*[:=]\s*[^\r\n]*/giu,
      'authorization=[redacted]'
    )
    .replace(/\b(cookie|set-cookie)\s*[:=]\s*[^\r\n]+/giu, '$1=[redacted]')
    .replace(
      /\b(bearer|token|secret|password|api[-_]?key)\s*[:=]?\s*[^\s,;]+/giu,
      '$1=[redacted]'
    )
    .replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/giu, '[email-redacted]')
    .replace(/https?:\/\/[^\s)]+/giu, raw => {
      try {
        const url = new URL(raw);
        return `${url.origin}${url.pathname}`;
      } catch {
        return '[url-redacted]';
      }
    })
    .slice(0, maxLength);
}

export class HelpActionsService extends Service {
  constructor(
    private readonly dialogs: GlobalDialogService,
    private readonly urls: UrlService
  ) {
    super();
  }

  reportBug(prefill?: {
    title?: string;
    description?: string;
    errorStack?: string;
  }) {
    this.dialogs.open('feedback', {
      type: 'bug',
      prefill: prefill
        ? {
            title: sanitizeFeedbackPrefill(prefill.title, 120),
            description: sanitizeFeedbackPrefill(prefill.description, 5_000),
            errorStack: sanitizeFeedbackPrefill(prefill.errorStack),
          }
        : undefined,
    });
  }

  submitIdea() {
    this.dialogs.open('feedback', { type: 'idea' });
  }

  openStatus(locale: WebsiteLocale) {
    this.openWebsitePage('status', locale);
  }

  openReleaseNotes(locale: WebsiteLocale) {
    this.openWebsitePage('releaseNotes', locale);
  }

  /** The platform handbook on the website of the build. */
  openPlatformDocs(locale: WebsiteLocale) {
    this.openWebsitePage('platformDocs', locale);
  }

  private openWebsitePage(page: WebsitePage, locale: WebsiteLocale) {
    this.urls.openPopupWindow(
      resolveWebsiteUrl(BUILD_CONFIG.websiteBaseUrl, page, locale)
    );
  }
}
